import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Typography,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { batchSyncProgramVersionItems } from '../../../api/services/challenge.service';
import { BatchSyncResult } from '../../../api/types/dtos/challenge/ChallengeDto';
import { useAllProgramVersionsDetailed } from '../../../api/hooks/program.hooks';
import { useOrganizationsListPaginated } from '../../../api/hooks/useOrganizations';
import { fetchOrganizationsListPaginated, OrganizationListDto } from '../../../api/services/organization.service';
import { useTablePagination, useSearchWithPagination } from '../../../api/hooks/useTablePagination';

interface SyncProgramVersionItemsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CONFIRMATION_TEXT = 'I understand this modification will affect the current challenge';

const SyncProgramVersionItemsModal: React.FC<SyncProgramVersionItemsModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id || '';
  
  // Step state
  const [step, setStep] = useState(1);
  
  // Step 1: Program version selection
  const [selectedProgramVersionId, setSelectedProgramVersionId] = useState<string>('');
  const { data: programVersions = [], isLoading: pvLoading } = useAllProgramVersionsDetailed(organizationId);
  
  // Step 2: Organization selection
  const [selectedOrgs, setSelectedOrgs] = useState<OrganizationListDto[]>([]);
  const [selectingAllMatching, setSelectingAllMatching] = useState(false);
  
  // Step 3: Confirmation and execution
  const [confirmationText, setConfirmationText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BatchSyncResult | null>(null);

  // Pagination state
  const {
    page,
    pageSize,


    resetPagination,
  } = useTablePagination({
    initialPage: 0,
    initialPageSize: 25,
  });

  // Search state with debouncing
  const {
    searchTerm,
    debouncedSearchTerm,
    setSearchTerm,
    clearSearch,
  } = useSearchWithPagination(1000, resetPagination);

  // Fetch organizations
  const {
    data: orgListData,
    isLoading: orgListLoading,
  } = useOrganizationsListPaginated(
    organizationId,
    {
      page,
      size: pageSize,
      searchTerm: debouncedSearchTerm || undefined,
    },
    {
      enabled: !!organizationId && step === 2
    }
  );

  // Get current page organizations
  const currentPageOrgs = useMemo(() => {
    if (!orgListData || !orgListData.data) return [];
    const content = orgListData.data.content;
    return Array.isArray(content) ? content : [];
  }, [orgListData]);

  // Check if all current page orgs are selected
  const allCurrentPageSelected = useMemo(() => {
    if (!Array.isArray(currentPageOrgs) || currentPageOrgs.length === 0) return false;
    return currentPageOrgs.every(org => selectedOrgs.some(sel => sel.id === org.id));
  }, [currentPageOrgs, selectedOrgs]);

  // Check if some (but not all) current page orgs are selected
  const someCurrentPageSelected = useMemo(() => {
    if (!Array.isArray(currentPageOrgs) || currentPageOrgs.length === 0) return false;
    return currentPageOrgs.some(org => selectedOrgs.some(sel => sel.id === org.id)) && !allCurrentPageSelected;
  }, [currentPageOrgs, selectedOrgs, allCurrentPageSelected]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedProgramVersionId('');
      setSelectedOrgs([]);
      setConfirmationText('');
      setError(null);
      setResult(null);
      clearSearch();
      resetPagination();
    }
  }, [open, clearSearch, resetPagination]);

  // Handle select all on current page
  const handleSelectAllCurrentPage = (checked: boolean) => {
    if (!Array.isArray(currentPageOrgs)) return;
    
    if (checked) {
      const newSelected = [...selectedOrgs];
      currentPageOrgs.forEach(org => {
        if (!newSelected.some(sel => sel.id === org.id)) {
          newSelected.push(org);
        }
      });
      setSelectedOrgs(newSelected);
    } else {
      setSelectedOrgs(selectedOrgs.filter(sel => !currentPageOrgs.some(org => org.id === sel.id)));
    }
  };

  // Handle select all matching filter
  const handleSelectAllMatchingFilter = useCallback(async () => {
    if (!organizationId) return;
    
    setSelectingAllMatching(true);
    try {
      const allOrgs: OrganizationListDto[] = [];
      let currentPage = 0;
      let hasMore = true;
      const pageSizeForFetch = 100;

      while (hasMore) {
        const response = await fetchOrganizationsListPaginated(
          organizationId,
          {
            page: currentPage,
            size: pageSizeForFetch,
            searchTerm: debouncedSearchTerm || undefined,
          }
        );

        if (response.data && response.data.content && Array.isArray(response.data.content) && response.data.content.length > 0) {
          allOrgs.push(...response.data.content);
          currentPage++;
          
          if (response.data.content.length < pageSizeForFetch || currentPage >= response.data.totalPages) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      setSelectedOrgs(prev => {
        const newSelected = [...prev];
        allOrgs.forEach(org => {
          if (!newSelected.some(sel => sel.id === org.id)) {
            newSelected.push(org);
          }
        });
        return newSelected;
      });
    } catch (error) {
      console.error('Error selecting all matching organizations:', error);
    } finally {
      setSelectingAllMatching(false);
    }
  }, [organizationId, debouncedSearchTerm]);

  const handleToggleOrg = (org: OrganizationListDto) => {
    setSelectedOrgs(prev =>
      prev.some(sel => sel.id === org.id)
        ? prev.filter(sel => sel.id !== org.id)
        : [...prev, org]
    );
  };

  const handleRemoveOrg = (orgId: string) => {
    setSelectedOrgs(prev => prev.filter(sel => sel.id !== orgId));
  };

  const isConfirmationValid = useMemo(() => {
    return confirmationText.trim().toLowerCase() === CONFIRMATION_TEXT.toLowerCase();
  }, [confirmationText]);

  const selectedProgramVersion = useMemo(() => {
    return programVersions.find(pv => pv.id === selectedProgramVersionId);
  }, [programVersions, selectedProgramVersionId]);

  const handleClose = () => {
    onClose();
  };

  const handleSync = async () => {
    if (!organizationId || !selectedProgramVersionId || !isConfirmationValid || selectedOrgs.length === 0) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await batchSyncProgramVersionItems(
        organizationId,
        selectedProgramVersionId,
        selectedOrgs.map(org => org.id)
      );

      if (response.data) {
        setResult(response.data);
        if (response.data.totalCreated > 0) {
          setTimeout(() => {
            onSuccess();
            handleClose();
          }, 3000);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Error al sincronizar los items del programa');
    } finally {
      setIsLoading(false);
    }
  };

  const canProceedToStep2 = selectedProgramVersionId !== '';


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" />
        Sincronizar Items de Programa - Paso {step} de 3
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, minHeight: 400 }}>
          {/* Step 1: Select Program Version */}
          {step === 1 && (
            <>
              <Alert severity="info">
                <Typography variant="body2">
                  Selecciona la version de programa que ha sido modificada. Los items nuevos de esta version 
                  seran agregados a las fincas que selecciones en el siguiente paso.
                </Typography>
              </Alert>

              <FormControl fullWidth required>
                <InputLabel>Version de Programa a Actualizar</InputLabel>
                <Select
                  value={selectedProgramVersionId}
                  onChange={(e) => setSelectedProgramVersionId(e.target.value)}
                  label="Version de Programa a Actualizar"
                  disabled={pvLoading}
                >
                  {programVersions.map((pv) => (
                    <MenuItem key={pv.id} value={pv.id}>
                      {pv.programId ? `${pv.programId} - ${pv.name}` : pv.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedProgramVersion && (
                <Alert severity="success">
                  <Typography variant="body2">
                    <strong>Seleccionado:</strong> {selectedProgramVersion.programId} - {selectedProgramVersion.name}
                  </Typography>
                </Alert>
              )}
            </>
          )}

          {/* Step 2: Select Organizations */}
          {step === 2 && (
            <>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Programa seleccionado:</strong> {selectedProgramVersion?.programId} - {selectedProgramVersion?.name}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selecciona las organizaciones (fincas) que deseas actualizar. Para cada finca, 
                  se buscaran todos los retos que contengan esta version de programa y se agregaran los items nuevos.
                </Typography>
              </Alert>

              {/* Search and selection controls */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  label="Buscar Organizacion"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  fullWidth
                  placeholder="Nombre, ID, grupo, dueno o telefono"
                  size="small"
                />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allCurrentPageSelected}
                        indeterminate={someCurrentPageSelected}
                        onChange={e => handleSelectAllCurrentPage(e.target.checked)}
                        disabled={orgListLoading || currentPageOrgs.length === 0 || selectingAllMatching}
                      />
                    }
                    label="Seleccionar todos los de esta pagina"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={false}
                        onChange={() => handleSelectAllMatchingFilter()}
                        disabled={orgListLoading || selectingAllMatching}
                      />
                    }
                    label={
                      selectingAllMatching ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <span>Seleccionando...</span>
                        </Box>
                      ) : (
                        'Seleccionar todos los de este filtro'
                      )
                    }
                  />
                </Box>
              </Box>

              {/* Two-column layout */}
              <Grid container spacing={2} sx={{ flexGrow: 1, minHeight: 300 }}>
                {/* Left column: Available organizations */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Organizaciones Disponibles
                    </Typography>
                    {orgListLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #eee', borderRadius: 1 }} disablePadding>
                        {currentPageOrgs.map(org => {
                          const isSelected = selectedOrgs.some(sel => sel.id === org.id);
                          return (
                            <ListItem
                              key={org.id}
                              disablePadding
                              secondaryAction={
                                <ListItemIcon>
                                  <Checkbox edge="end" checked={isSelected} />
                                </ListItemIcon>
                              }
                            >
                              <ListItemButton
                                onClick={() => handleToggleOrg(org)}
                                selected={isSelected}
                              >
                                <ListItemText
                                  primary={`${org.name} (${org.idNumber})`}
                                  secondary={`Dueno: ${org.owner?.name || 'N/A'} | Grupos: ${org.groupNames?.join(', ') || 'N/A'}`}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                        {currentPageOrgs.length === 0 && (
                          <ListItem>
                            <ListItemText primary="No se encontraron organizaciones." />
                          </ListItem>
                        )}
                      </List>
                    )}
                  </Box>
                </Grid>

                {/* Right column: Selected organizations */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Organizaciones Seleccionadas ({selectedOrgs.length})
                    </Typography>
                    <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #eee', borderRadius: 1 }} disablePadding>
                      {selectedOrgs.map(org => (
                        <ListItem
                          key={org.id}
                          secondaryAction={
                            <IconButton edge="end" onClick={() => handleRemoveOrg(org.id)} size="small">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={`${org.name} (${org.idNumber})`}
                            secondary={`Dueno: ${org.owner?.name || 'N/A'}`}
                          />
                        </ListItem>
                      ))}
                      {selectedOrgs.length === 0 && (
                        <ListItem>
                          <ListItemText primary="No hay organizaciones seleccionadas." />
                        </ListItem>
                      )}
                    </List>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}

          {/* Step 3: Confirm and Execute */}
          {step === 3 && (
            <>
              <Alert severity="warning">
                <Typography variant="body2">
                  Esta operacion creara nuevos items de programa para las fincas seleccionadas.
                  Los items existentes <strong>NO</strong> seran modificados ni eliminados.
                  Los nuevos items se crearan con estado <strong>pendiente (inactivo)</strong>.
                </Typography>
              </Alert>

              {/* Summary */}
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Resumen de la Operacion
                </Typography>
                <Typography variant="body2">
                  <strong>Programa:</strong> {selectedProgramVersion?.programId} - {selectedProgramVersion?.name}
                </Typography>
                <Typography variant="body2">
                  <strong>Fincas seleccionadas:</strong> {selectedOrgs.length}
                </Typography>
              </Box>

              {/* Selected farms list */}
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Fincas a actualizar:
                </Typography>
                <List dense>
                  {selectedOrgs.slice(0, 10).map(org => (
                    <ListItem key={org.id}>
                      <ListItemText primary={org.name} secondary={org.idNumber} />
                    </ListItem>
                  ))}
                  {selectedOrgs.length > 10 && (
                    <ListItem>
                      <ListItemText primary={`...y ${selectedOrgs.length - 10} fincas mas`} />
                    </ListItem>
                  )}
                </List>
              </Box>

              {/* Confirmation text field */}
              <TextField
                label="Escribe el texto de confirmacion"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                fullWidth
                required
                disabled={isLoading || !!result}
                helperText={`Escribe: "${CONFIRMATION_TEXT}"`}
                error={confirmationText.length > 0 && !isConfirmationValid}
              />

              {/* Error display */}
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Success result display */}
              {result && (
                <Alert severity={result.errors.length > 0 ? 'warning' : 'success'}>
                  <Typography variant="body2">
                    <strong>Resultado de la sincronizacion:</strong>
                  </Typography>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Total items creados: {result.totalCreated}</li>
                    <li>Total items omitidos: {result.totalSkipped}</li>
                    <li>Fincas actualizadas: {result.farmResults.length}</li>
                    {result.errors.length > 0 && (
                      <li>Errores: {result.errors.length}</li>
                    )}
                  </ul>
                  {result.farmResults.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" fontWeight="bold">Detalle por finca:</Typography>
                      {result.farmResults.slice(0, 5).map((farm, i) => (
                        <Typography key={i} variant="body2">
                          {farm.farmName}: {farm.created} creados, {farm.skipped} omitidos ({farm.challengesUpdated} retos)
                        </Typography>
                      ))}
                      {result.farmResults.length > 5 && (
                        <Typography variant="body2">...y {result.farmResults.length - 5} fincas mas</Typography>
                      )}
                    </Box>
                  )}
                  {result.totalCreated > 0 && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Cerrando automaticamente...
                    </Typography>
                  )}
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {result ? 'Cerrar' : 'Cancelar'}
        </Button>
        {step > 1 && !result && (
          <Button onClick={() => setStep(step - 1)} disabled={isLoading}>
            Atras
          </Button>
        )}
        {step === 1 && !result && (
          <Button
            variant="contained"
            onClick={() => setStep(2)}
            disabled={!canProceedToStep2}
          >
            Siguiente
          </Button>
        )}
        {step === 2 && !result && (
          <Button
            variant="contained"
            onClick={() => setStep(3)}
            disabled={!canProceedToStep2}
          >
            Siguiente
          </Button>
        )}
        {step === 3 && !result && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSync}
            disabled={!isConfirmationValid || isLoading}
          >
            {isLoading ? <CircularProgress size={20} /> : 'Sincronizar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SyncProgramVersionItemsModal;
